import {useFeatureIsOn} from '@growthbook/growthbook-react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import BugReportIcon from '@material-ui/icons/BugReport';
import {Alert} from '@material-ui/lab';
import prettify from 'html-prettify';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react-lite';
import {Fragment, useEffect, useState} from 'react';
import {SubmitHandler, useFormContext} from 'react-hook-form';
import API from '../../APIs/API';
import {User} from '../../Auth/User';
import {AgeComputed} from '../../Core/helpers/computed/Age';
import {useAgentAdminData} from '../../Core/hooks/useAgentAdminData';
import {ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import RootStore from '../../Core/mobx/RootStore';
import {TemplateDataFields} from '../ContactForm/EmailEditor/EmailEditor';
import CustomerPortalStore from '../Customer/CustomerPortalStore/CustomerPortalStore';
import {ProsperFormValues} from './ProductTabs/lib/validation';
import {QuotesTable} from './QuotesTable';
import {calcfocusAPI} from './api/calcfocus';
import {guaranteedAPI} from './api/guarenteed';
import {initInstantIulApplication} from './api/instantIul';
import {ixnAPI} from './api/ixn';
import {lswAPI} from './api/lsw';
import {fgWinflexAPI} from './api/FGwinflex';
import {tafeAPI} from './api/tafe';
import {winflexAPI} from './api/winflex';
import {PROSPER_MAX_AGE_LIMIT, PROSPER_MIN_AGE_LIMIT} from './constants';
import {useApplicationData} from '@/Core/hooks/useApplicationData';
import {v4} from 'uuid';
import AgentAssistPanel from '../Assist/AgentAssistPanel';
import {adbAPI} from './api/adb';
import {quoteGuards} from '@/Core/mobx/ParameterStore/QuoteGuards';

const FORM_TYPE_SINGLE_QUOTE = 'single-quote';
const FORM_TYPE_COMBINATION_QUOTE = 'combination-quote';

// TODO this "API" file is a combination of UI elements and
// really complex API calls. Split these out.
export const QuoteAPI = observer(
    ({
        parameterStore,
        formType,
    }: {
        parameterStore: ParameterStore;
        formType?: 'single-quote' | 'combination-quote';
        customerPortalStore: CustomerPortalStore;
    }) => {
        const {agentData, loading: agentDataLoading} = useAgentAdminData();
        const {updateApplicationData} = useApplicationData();

        const [reqDialog, setReqDialog] = useState(false);
        const [status, setStatus] = useState('');
        const email = RootStore.agentPortalStore.contactsFormStore.contact;
        const customerName = RootStore.agentPortalStore.contactsFormStore.name;
        const name = parameterStore.approval.firstName.value as string;
        const contactName = parameterStore.name || '';
        const [dataReceived, setDataReceived] = useState(false);
        const [startPolling, setStartPolling] = useState(false);
        // prod may have different restrictions while we test ins taging
        const prosperNotAllowedStates = ['CA', 'NJ', 'NY'];
        const [vulData, setVulData] = useState({
            getQuote: true,
            uuid: '' as any,
            reqData: {} as any,
            resData: {} as any,
        });
        const [loading, setLoading] = useState(false);
        const [data, setData] = useState(
            null as {
                error?: string;
                req: string;
                changes: Array<{Keyword: string; OriginalAnswer: string; ValidAnswer: string}>;
                out: Array<{name: string; value: any}>;
            } | null,
        );
        const LSW_POLL_BOOLEAN = useFeatureIsOn('new-lsw-quote-api');

        function makeRequestObjectForVUL() {
            const firstName = parameterStore.approval.firstName.value;
            const lastName = parameterStore.approval.lastName.value;
            const bday = parameterStore.approval.bday.value;
            const bmonth = parameterStore.approval.bmonth.value;
            const byear = parameterStore.approval.byear.value;
            const nearestAge = AgeComputed(bday, bmonth, byear);

            let dob_string = `${bmonth}/${bday}/${byear}`,
                gender = parameterStore.approval.gender.value,
                state = parameterStore.approval.state.value,
                main_rate = parameterStore.getQuote(formType)?.mainRateVUL.value.toString(),
                premium_frequency_name = parameterStore.getQuote(formType)?.premiumFrequency.value,
                premium_frequency,
                amount = '',
                solve_scenario_string = parameterStore.getQuote(formType)?.solveScenarioVUL.value,
                solve_scenario,
                compliance_test_string = parameterStore.getQuote(formType)?.complianceTestVUL.value,
                compliance_test,
                mec_test = parameterStore.getQuote(formType)?.mecAvoidance.value,
                pay_to_age = parameterStore.getQuote(formType)?.premiumPayToAgeVUL.value.toString(),
                flat_extra = parameterStore.getQuote(formType)?.flatExtra.value,
                table_rating_string = parameterStore.getQuote(formType)?.tableRatingAmeritasVUL.value,
                table_rating = table_rating_string === 'None' ? 'none' : table_rating_string[6],
                lifetime_flat_extra = parameterStore.getQuote(formType)?.flatExtraPerm.value.toString(),
                temp_flat_extra = parameterStore.getQuote(formType)?.flatExtraTemp.value.toString(),
                temp_flat_extra_years = parameterStore.getQuote(formType)?.flatExtraYears.value.toString(),
                death_option_string = parameterStore.getQuote(formType)?.deathBenefitOptionVUL.value,
                death_option,
                death_option_change_age = parameterStore.getQuote(formType)?.deathBenefitChangeAgeVUL.value.toString(),
                tobAttribute: 'healthRatingAmeritasJuvenileVUL' | 'healthRatingAmeritasAdultVUL' =
                    nearestAge < 18 ? 'healthRatingAmeritasJuvenileVUL' : 'healthRatingAmeritasAdultVUL',
                tobacco = parameterStore.getQuote(formType)?.[tobAttribute].value;
            death_option_change_age = death_option_change_age === undefined ? '' : death_option_change_age;
            switch (solve_scenario_string) {
                case 'Setpremiumsolveforminimumnonmecfaceamount':
                    solve_scenario = '1';
                    break;
                case 'Setfaceamountsolveformaximumnonmecpremium':
                    solve_scenario = '2';
                    break;
                case 'Setpremiumsolvefortargetfaceamount':
                    solve_scenario = '3';
                    break;
                case 'Setfaceamountsolvefortargetpremium':
                    solve_scenario = '4';
                    break;
                default:
                    solve_scenario = '1';
            }
            compliance_test = compliance_test_string === 'GuidelinePremiumTest' ? false : true;
            amount = (
                solve_scenario === '1' || solve_scenario === '3'
                    ? parameterStore.getQuote(formType)?.premiumAmountVUL.value
                    : parameterStore.getQuote(formType)?.faceAmountVUL.value
            ).toString();
            switch (premium_frequency_name) {
                case 'monthly':
                    premium_frequency = '4';
                    break;
                case 'quarterly':
                    premium_frequency = '3';
                    break;
                case 'semiannual':
                    premium_frequency = '2';
                    break;
                case 'annual':
                    premium_frequency = '1';
                    break;
                default:
                    premium_frequency = '4';
            }
            switch (death_option_string) {
                case 'Increasing':
                    death_option = '1';
                    break;
                case 'Level':
                    death_option = '2';
                    break;
                case 'IncreasingthenLevel':
                    death_option = '3';
                    break;
                default:
                    death_option = '1';
            }
            return {
                dob_string,
                gender,
                state,
                tobacco,
                main_rate,
                premium_frequency,
                amount,
                solve_scenario,
                compliance_test,
                mec_test,
                pay_to_age,
                responseType: 'GET-DATA',
                flat_extra,
                table_rating,
                lifetime_flat_extra,
                temp_flat_extra,
                temp_flat_extra_years,
                death_option,
                death_option_change_age,
                output_data: {},
                firstName,
                lastName,
                // Below fields for Zeus runtime and not for quote request

                nearestAge,
                bday,
                bmonth,
                byear,
            };
        }

        useEffect(() => {
            (async () => {
                if (vulData.getQuote === false) {
                    vulData.getQuote = true;
                    vulData.reqData = makeRequestObjectForVUL();
                    vulData.uuid = (await API.Call<{uuid: string}>('vul-task/create', 'POST', vulData.reqData))?.uuid;
                    setVulData({...vulData});
                    setStartPolling(true);
                }
            })();
        }, [vulData]);

        useEffect(() => {
            (async () => {
                if (startPolling === true) {
                    const fiveSeconds = 5000;
                    (() => {
                        const timerVariable = setInterval(async () => {
                            let dataVUL: any = await API.Call('vul-task/get-task', 'POST', {uuid: vulData.uuid});
                            if (dataVUL !== null) {
                                let {fileName} = dataVUL.v;
                                dataVUL = dataVUL.v.output_data[0];
                                // The data should be mainpulated here;
                                let currCashValue: any = [];
                                let currSurrenderValue: any = [];
                                dataVUL.vulList.forEach((entry: any) => {
                                    currCashValue.push(entry.Curr_Cash_Val);
                                    currSurrenderValue.push(entry.Current_Surrender_Val);
                                });
                                let {pdfFileURL}: any = await API.Call('read-vul-pdf', 'POST', {
                                    fileName,
                                });
                                vulData.resData = {
                                    out: [
                                        {
                                            id: 'CompanyName',
                                            label: 'Carrier',
                                            name: 'Carrier',
                                            type: 'string',
                                            value: 'Ameritas Life Insurance Corp.',
                                            fieldName: TemplateDataFields.carrier,
                                        },
                                        {
                                            id: 'Policy',
                                            label: 'Product name',
                                            name: 'Product name',
                                            type: 'string',
                                            value: 'Advisor II VUL',
                                            fieldName: TemplateDataFields.productName,
                                        },
                                        {
                                            id: 'InitialAnnPrem',
                                            label: 'Initial annual premium',
                                            name: 'Initial annual premium',
                                            type: 'currency',
                                            value: dataVUL.vulList[0].Premium,
                                            fieldName: TemplateDataFields.monthlyPremium,
                                        },
                                        {
                                            id: 'InitialMonthlyPrem',
                                            label: 'Initial Monthly premium',
                                            name: 'Initial Monthly premium',
                                            type: 'currency',
                                            value: dataVUL.vulList[0].Premium / 12,
                                            fieldName: TemplateDataFields.monthlyPremium,
                                        },
                                        {
                                            id: 'InitialDB',
                                            label: 'Initial death benefit',
                                            name: 'Initial death benefit',
                                            type: 'currency',
                                            value: dataVUL.vulList[0].Current_Death_Benefit,
                                            fieldName: TemplateDataFields.faceAmount,
                                        },
                                        {
                                            id: 'LapseYear',
                                            label: 'Lapse year',
                                            name: 'Lapse year',
                                            type: 'year',
                                            value: 121,
                                            fieldName: TemplateDataFields.termPeriod,
                                        },
                                        {
                                            id: 'AnnualPremium',
                                            label: 'Annual premium',
                                            name: 'Annual premium',
                                            type: 'currency',
                                            value: dataVUL.vulList[0].Premium,
                                            fieldName: TemplateDataFields.monthlyPremium,
                                        },
                                        {
                                            id: 'CashVal60',
                                            label: 'CashVal60',
                                            name: 'Cash value at age 60',
                                            type: 'currency',
                                            value:
                                                vulData.reqData.nearestAge <= 59
                                                    ? dataVUL.vulList[60 - vulData.reqData.nearestAge - 1].Curr_Cash_Val
                                                    : 0,
                                        },
                                        {
                                            id: 'CashVal65',
                                            label: 'CashVal65',
                                            name: 'Cash value at age 65',
                                            type: 'currency',
                                            value:
                                                vulData.reqData.nearestAge <= 64
                                                    ? dataVUL.vulList[65 - vulData.reqData.nearestAge - 1].Curr_Cash_Val
                                                    : 0,
                                        },
                                        {
                                            id: 'CashVal70',
                                            label: 'CashVal70',
                                            name: 'Cash value at age 70',
                                            type: 'currency',
                                            value:
                                                vulData.reqData.nearestAge <= 69
                                                    ? dataVUL.vulList[70 - vulData.reqData.nearestAge - 1].Curr_Cash_Val
                                                    : 0,
                                            fieldName: TemplateDataFields.cashValue,
                                        },
                                        {
                                            id: 'cash_value',
                                            label: 'cash_value',
                                            name: 'Cash Value',
                                            type: 'currency',
                                            value: currCashValue,
                                        },
                                        {
                                            id: 'net_surrender_value',
                                            label: 'net_surrender_value',
                                            name: 'Net Surrender Value',
                                            type: 'currency',
                                            value: currSurrenderValue,
                                        },
                                        {
                                            name: 'Illustration',
                                            value: pdfFileURL,
                                            type: 'string',
                                        },
                                    ],
                                    faceAmount: dataVUL.vulList[0].Current_Death_Benefit,
                                    monthlyPremium: dataVUL.vulList[0].Premium / 12,
                                    productName: 'Advisor II VUL',
                                    req: null,
                                };

                                setVulData({...vulData});
                                clearInterval(timerVariable);
                                setStartPolling(false);
                                setDataReceived(true);
                            }
                        }, fiveSeconds);
                    })();
                }
            })();
        }, [startPolling]);

        useEffect(() => {
            if (dataReceived) {
                let resp = vulData.resData;
                quoteDataReceived(resp, 'VUL');
                setData(resp);
                setLoading(false);
                setDataReceived(false);
            }
        }, [dataReceived]);

        const GetQuote = async (prosperData?: ProsperFormValues) => {
            setLoading(true);
            setData(null);

            const productType = parameterStore.getQuote(formType)?.productType.value;
            const ameritas = parameterStore.getQuote(formType)?.ameritas.value;
            const moo = parameterStore.getQuote(formType)?.moo.value;
            const fg = parameterStore.getQuote(formType)?.fg.value;
            console.log({productType});

            setStatus('');

            let localVariableTemp = true; // this variable ensures that except 'VUL', resp data is set as earlier implementation.
            let resp: any = null;
            const productWHL = parameterStore.getQuote(formType)?.productWHL.value;
            const isTAFE = productType === 'Whole Life' && productWHL === 'TAFEExpress';

            if ((productType === 'IUL' && (ameritas || moo || fg)) || (productType === 'Whole Life' && moo)) {
                if (fg) {
                    resp = await fgWinflexAPI(parameterStore, customerName, agentData, formType);
                } else {
                    resp = await winflexAPI(parameterStore, customerName, agentData, formType, (msg) => {
                        setStatus(msg);
                    });
                }
                setStatus('');
            } else if (isTAFE) {
                resp = await tafeAPI(parameterStore, formType);
            } else if (productType === 'Guaranteed') {
                resp = await guaranteedAPI(parameterStore, formType);
            } else if (productType === 'VUL') {
                setVulData({...vulData, getQuote: false});
                localVariableTemp = false;
            } else if (productType === 'LSWIUL') {
                resp = await lswAPI(parameterStore, LSW_POLL_BOOLEAN);
            } else if (productType === 'Prosper') {
                let formId;
                if (!parameterStore.formid) {
                    formId = v4();
                    RootStore.agentPortalStore.contactsFormStore.setFormId(formId);
                }
                const email = parameterStore.contact || parameterStore.approval.emailAddress.value;
                const applicationId = await initInstantIulApplication(parameterStore.formid, {
                    email,
                });
                await updateApplicationData(
                    {hubspotId: RootStore.agentPortalStore.contactsFormStore.hs_object_id, prosperData},
                    formId,
                );
                parameterStore.instantIulApplicationId = applicationId;

                if (prosperData)
                    resp = await calcfocusAPI(
                        prosperData,
                        parameterStore,
                        RootStore.agentPortalStore.contactsFormStore,
                    );
            } else if (productType === 'ADB') {
                resp = await adbAPI(parameterStore);
            } else {
                resp = await ixnAPI(parameterStore, productType, formType);
            }

            if (localVariableTemp) {
                quoteDataReceived(resp, productType);
                setData(resp);
                setLoading(false);
            }
            localVariableTemp = true;

            await API.Call('v2/log/agent/GetQuote', 'POST', {
                clientEmail: parameterStore.approval.emailAddress.value,
                agentEmail: User.Info.email,
                quoteType: productType,
                requestDetails: resp?.requestDetails,
            });

            if (productType === 'Prosper') {
                await RootStore.agentPortalStore.contactsFormStore.saveForm();
            }
        };

        function quoteDataReceived(quoteResult: any, productType: string) {
            const quoteId = Date.now();

            if (quoteResult?.req != null && quoteResult?.req != '' && quoteResult?.productName !== 'PROSPER') {
                quoteResult.req = prettify(quoteResult.req);
            }
            if (
                quoteResult?.out?.length ||
                quoteResult.policyInfo?.policySummary?.[0].scenarioSummary?.[0]?.endOfYearValues?.length
            ) {
                let quoteHistory = parameterStore?.quoteHistory;
                const isCombination = parameterStore?.quoteCombination.isCombination;

                const data = {
                    id: quoteId,
                    out: quoteResult.out,
                    faceAmount: quoteResult.faceAmount,
                    monthlyPremium: quoteResult.monthlyPremium,
                    productName: quoteResult.productName,
                    quoteAge: quoteResult.quoteAge,
                    lapseAge: quoteResult.lapseAge,
                    email,
                    name,
                    contactName,
                    productType,
                };

                runInAction(() => {
                    // currently, all the data about policy is stored in one document. The problem with this approach
                    // is that there isn't a way to receive anything asynchronously. For example, the quote or RX information takes a long time and
                    // the person moves away from the page, that request is lost. This is especially a problem with RX as you cannot request
                    // for data again. Future improvement for this is to have a separate table, which is retrieved by the policy document id
                    // which will allow us to save the returned data asynchronously (if supported) as well.

                    // In this function, as is currently implemented, there are two cases. Combination or single.
                    // if it was a combination, then we only want to push it to quoteHistory if we have both first and second quotes ready
                    // there are few edge cases we need to think about
                    // 1) what if the user had non-combination page, requested for a quote, and before the quote came back, clicked combination?
                    //     - this may not be an issue. Then, this will go to first quote, we basically add it as the first quote.
                    // 2) what if the user had combination page, requested for a quote for first quote, and before the quote came back, unchecked combination?
                    //     - this also may not be an issue. We will just show it as the first (and only) quote, push to history
                    // 3) what if the user had combination page, requested for a quote for second quote, and before the quote came back, unchecked combination?
                    //     - may not be an issue. We will just lose this quote
                    // 4) what if the user had combination page, requested for a second quote, and before the quote came back, unchecked, then checked combination?
                    //     - this may be an issue. Because while you have the result, the parameters may be lost. one solution might be to disable combination
                    //       checkbox when the quote is being run. I will not address it now but this is @ToDo
                    parameterStore.currentQuoteId = quoteId;

                    // regardless of which mode we are in, if it's a single quote, always update single quote
                    if (formType === FORM_TYPE_SINGLE_QUOTE) {
                        parameterStore.singleQuoteData = {...data};
                    }

                    // if the form was combination quote and we are in combination mode, then update the combined quote
                    if (formType === FORM_TYPE_COMBINATION_QUOTE && isCombination) {
                        parameterStore.combinedQuoteData = {...data};
                    }

                    // two cases when we can update the quoteHistory
                    // 1) we are in combination and we have both single and combined quuote data
                    // 2) we are in single quote and we received single quote data above
                    if (
                        (isCombination &&
                            parameterStore.singleQuoteData != null &&
                            parameterStore.combinedQuoteData != null) ||
                        (!isCombination && formType === FORM_TYPE_SINGLE_QUOTE)
                    ) {
                        let secondQuote = null;
                        if (isCombination && parameterStore.combinedQuoteData != null) {
                            secondQuote = parameterStore.combinedQuoteData;
                        }
                        quoteHistory.push({
                            id: quoteId,
                            email,
                            name,
                            contactName,
                            singleQuote: parameterStore.singleQuoteData,
                            combinedQuote: secondQuote,
                        });
                    }
                });
            }
        }

        const combinedQuote = parameterStore?.combinedQuoteData;
        const singleQuote = parameterStore?.singleQuoteData;
        const quoteId = parameterStore.currentQuoteId;

        let ariaLabelForGetQuoteButton = 'get-quote-button';
        if (formType == 'combination-quote') {
            ariaLabelForGetQuoteButton += '-comb';
        }

        const productType = parameterStore.getQuote(formType)?.productType.value;
        const {handleSubmit, formState} = useFormContext<ProsperFormValues>();

        const onSubmit: SubmitHandler<ProsperFormValues> = async (values) => {
            await GetQuote(values);
        };

        const invalidByADB = () => {
            return (
                (parameterStore.getQuote(formType)?.productType.value === 'ADB' &&
                    !parameterStore.getQuote(formType)?.productADB.value) ||
                quoteGuards.invalidStatesADB(parameterStore, parameterStore.getQuote(formType)!) ||
                quoteGuards.invalidReturnOnPremiumADB(parameterStore, parameterStore.getQuote(formType)!)
            );
        };

        return (
            <Grid item style={{marginTop: 20}}>
                <Grid item style={{position: 'sticky', top: 0}}>
                    {productType === 'Prosper' ? (
                        <div>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit(onSubmit)}
                                disabled={
                                    !formState.isValid ||
                                    parameterStore.prequal.age.value < PROSPER_MIN_AGE_LIMIT ||
                                    parameterStore.prequal.age.value > PROSPER_MAX_AGE_LIMIT ||
                                    !parameterStore.prequal.state.value ||
                                    !!prosperNotAllowedStates.find((t) => t === parameterStore.prequal.state.value)
                                }
                            >
                                Get Prosper Quote
                            </Button>
                            {!!prosperNotAllowedStates.find((t) => t === parameterStore.prequal.state.value) && (
                                <Typography variant="caption" display="block" gutterBottom>
                                    State is not allowed
                                </Typography>
                            )}
                            {(parameterStore.prequal.age.value < PROSPER_MIN_AGE_LIMIT ||
                                parameterStore.prequal.age.value > PROSPER_MAX_AGE_LIMIT) && (
                                <Typography variant="caption" display="block" gutterBottom>
                                    This user does not qualify for Prosper IUL because of their age.
                                </Typography>
                            )}
                            {!parameterStore.prequal.state.value && (
                                <Typography variant="caption" display="block" gutterBottom>
                                    A state must be selected.
                                </Typography>
                            )}
                        </div>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            style={{width: '100%'}}
                            aria-label={ariaLabelForGetQuoteButton}
                            onClick={() => {
                                GetQuote();
                            }}
                            disabled={
                                agentDataLoading ||
                                loading ||
                                (productType === 'LSWIUL' &&
                                    !['kc@getamplifylife.com', 'vivianh@getamplifylife.com'].includes(
                                        User.Info.email,
                                    )) ||
                                invalidByADB()
                            }
                        >
                            GET Quote
                        </Button>
                    )}
                    <Box width="100%" textAlign="center" margin={20}>
                        {agentDataLoading || loading ? <CircularProgress /> : null}
                    </Box>
                    {data != null && data.req != null && data.req != '' ? (
                        <div style={{width: '100%', textAlign: 'right', marginBottom: 20, marginRight: 25}}>
                            <IconButton
                                aria-label="debug"
                                onClick={() => {
                                    setReqDialog(true);
                                }}
                            >
                                <BugReportIcon fontSize="large" />
                            </IconButton>
                        </div>
                    ) : null}
                </Grid>

                {status != '' ? <div>{status}</div> : null}

                {data != null && data.error != null ? <Alert>{data.error}</Alert> : null}

                <QuotesTable
                    formType={formType}
                    data={{id: quoteId!, singleQuote, combinedQuote}}
                    parameterStore={parameterStore}
                />

                <AgentAssistPanel
                    onValueChangedNotes={(value) =>
                        parameterStore.onValueChangedPreQual({...parameterStore.prequal.notes, name: 'notes'}, value)
                    }
                />

                <Dialog open={reqDialog} onClose={() => setReqDialog(false)} aria-labelledby="form-dialog-title">
                    <DialogTitle id="form-dialog-title">Winflex API Request</DialogTitle>
                    <DialogContent>
                        <div>
                            <div>
                                {data?.changes != null && data.changes.length > 0 ? (
                                    <Fragment>
                                        <h3>Corrected Fields</h3>
                                        <TableContainer component={Paper}>
                                            <Table style={{minWidth: '100%'}}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Input</TableCell>
                                                        <TableCell>Sent</TableCell>
                                                        <TableCell>Corrected</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {data.changes.map((x) => (
                                                        <TableRow key={x.Keyword}>
                                                            <TableCell>{x.Keyword}</TableCell>
                                                            <TableCell>{x.OriginalAnswer}</TableCell>
                                                            <TableCell>{x.ValidAnswer}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Fragment>
                                ) : null}
                            </div>
                            <div>
                                <h3>XML Request</h3>
                                <pre lang="xml">{data?.req}</pre>
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setReqDialog(false)} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Grid>
        );
    },
);
